import React, { useState, useRef, useEffect } from 'react';

import { Flex, useOutsideClick } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { AGENDA_CONDITIONS_TYPE } from 'screens/oneOnOneRabbit/SettingsPage/constants';
import { OneOnOneSettingsPageReactQueryKeys } from 'screens/oneOnOneRabbit/SettingsPage/constants/OneOnOneSettingsPageReactQueryKeys';
import { AdminAgenda } from 'screens/oneOnOneRabbit/SettingsPage/interfaces/AdminAgenda';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { z } from 'zod';

import { generateKey } from 'constants/helper';
import { sendClickedOnAddAdminAgendaEvent } from 'constants/mixpanelHelper';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';

import { EventTemplateAgendaType } from '../services';

type AdminAgenda = z.infer<typeof AdminAgenda>;

export const createAgendaObject = ({
  agendaText,
  agendaCondition,
  orgId,
}: {
  agendaText: string;
  agendaCondition: AGENDA_CONDITIONS_TYPE;
  orgId: number;
}): AdminAgenda & {
  uiId: string;
} => ({
  id: '',
  agendaText,
  agendaCondition,
  created: new Date(),
  displayOrder: -1,
  orgId,
  relationType: 'MANAGER',
  uiId: generateKey(8),
});

interface Props {
  setCreatingAgenda: (creatingAgenda: boolean) => void;
  agendaCondition: AGENDA_CONDITIONS_TYPE;
}

const CreateAdminAgenda = ({ setCreatingAgenda, agendaCondition }: Props) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [agendaValue, setAgendaValue] = useState('');

  const loggedInEmployeeData = useSelector(selectLoggedInEmployeeData);

  const orgId = Number(loggedInEmployeeData?.organization?.id);

  const queryClient = useQueryClient();

  const createAgendaHandler = async (agendaText: string) => {
    if (!agendaText || agendaText.trim() === '') {
      setCreatingAgenda(false);
      return;
    }
    try {
      const newAgenda = createAgendaObject({
        agendaText,
        agendaCondition,
        orgId,
      });

      if (agendaCondition === 'REGULAR') {
        queryClient.setQueryData<AdminAgenda[]>(
          [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'REGULAR'],
          (oldData) => {
            const agendaToBeAdded = { ...newAgenda, displayOrder: oldData?.length ?? 0 };
            return [...(oldData || []), agendaToBeAdded];
          }
        );
      } else {
        queryClient.setQueryData<EventTemplateAgendaType>(
          [OneOnOneSettingsPageReactQueryKeys.oneOnOneAdminAgenda, 'EVENT_BASED'],
          (oldData) => {
            const agendaToBeAdded = {
              ...newAgenda,
              displayOrder: oldData[agendaCondition]?.length ?? 0,
            };
            return {
              ...oldData,
              [agendaCondition]: [...(oldData[agendaCondition] || []), agendaToBeAdded],
            };
          }
        );
      }
    } catch (e) {
      console.error('Error creating agenda : ', e);
    } finally {
      setCreatingAgenda(false);
    }
  };

  const handleAgendaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value.slice(0, 255);
    setAgendaValue(inputValue);
  };

  useOutsideClick({
    ref: inputRef,
    handler: () => createAgendaHandler(agendaValue),
  });

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      sendClickedOnAddAdminAgendaEvent({
        eventProperties: {
          event: agendaCondition,
          type: 'Custom',
        },
      });
      createAgendaHandler(agendaValue);
    }
  };

  //   focus on input when component mounts
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <Flex
      p="18px 24px"
      sx={{
        backgroundColor: '#F6FAFE',
      }}
      w="100%"
      minHeight="56px"
      alignItems="center"
    >
      <AutoResizeInput
        ref={inputRef}
        isDashed
        value={agendaValue}
        onChange={handleAgendaChange}
        onKeyDown={onEnterPress}
      />
    </Flex>
  );
};

export { CreateAdminAgenda };
